/**
 * Technical Performance → Availability.
 *
 * Surfaces the Warranted Minimum Availability tracking inside the asset's
 * Technical Performance workspace (it's a technical metric). Resolves the
 * selected asset's O&M contract (preferring the one marked active) and reuses
 * the OmAvailability tile, which pulls the guarantee terms + the AEMO-derived
 * (or operator-reported) EPEBA for that contract.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Stack, Typography, Alert, Skeleton, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

import AssetPicker from './AssetPicker';
import TechnicalWorkspaceTabs from './TechnicalWorkspaceTabs';
import OmAvailability from './OmAvailability';
import { useThemeColors } from '../theme/ThemeModeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

export default function TechnicalAvailability() {
  const { portfolioId } = useParams();
  const colors = useThemeColors();

  const [assets, setAssets] = useState([]);
  const [assetId, setAssetId] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/overview`, { params: { period: '30d' } }),
      axios.get(`${API_BASE_URL}/api/contracts`, { params: { portfolio_id: portfolioId } }),
    ])
      .then(([ov, cs]) => {
        if (cancelled) return;
        const list = ov.data?.assets || [];
        setAssets(list);
        setAssetId((prev) => prev || (list[0] && list[0].asset_id) || null);
        setContracts(cs.data?.contracts || []);
      })
      .catch(() => { if (!cancelled) { setAssets([]); setContracts([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, reload]);

  // The asset's O&M contract — prefer the one marked active.
  const omContract = useMemo(() => {
    const want = (assetId || '').toUpperCase();
    const matches = (contracts || []).filter(
      (c) => c.contract_kind === 'O&M'
        && (c.asset_ids || []).map((a) => (a || '').toUpperCase()).includes(want),
    );
    return matches.find((c) => c.active) || matches[0] || null;
  }, [contracts, assetId]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <TechnicalWorkspaceTabs />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{
          fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em', color: 'text.primary',
        }}>
          Availability
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {assets.length > 1 && (
            <AssetPicker
              value={assetId || ''}
              onChange={setAssetId}
              assets={assets}
              colors={colors}
              allowAll={false}
              minWidth={220}
              placeholder="Select asset"
            />
          )}
          <IconButton
            size="small"
            onClick={() => setReload((n) => n + 1)}
            disabled={loading}
            sx={{ color: 'text.secondary' }}
            title="Refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {loading ? (
        <Skeleton variant="rounded" height={280} sx={{ bgcolor: colors.skeleton }} />
      ) : omContract ? (
        <OmAvailability contractId={omContract.contract_id} />
      ) : (
        <Alert severity="info" variant="outlined">
          No O&amp;M contract found for {assetId || 'this asset'}. Upload the asset’s O&amp;M agreement
          (it’s classified and extracted automatically) and the Warranted Minimum Availability
          guarantee + tracking will appear here.
        </Alert>
      )}
    </Box>
  );
}
